import { useEffect, useState } from 'react'
import { useHighlight } from '@/hooks/useHighlight'
import Button from '@/components/Button'

interface CodeSlideOverProps {
  isOpen: boolean
  onClose: () => void
  code: string
  title?: string
}

export default function CodeSlideOver({
  isOpen,
  onClose,
  code,
  title = 'Widget Initialization Code'
}: CodeSlideOverProps) {
  const codeRef = useHighlight([code, isOpen])
  const [copied, setCopied] = useState(false)

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div
          className={`relative w-screen max-w-2xl transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex h-full flex-col overflow-y-auto bg-white dark:bg-licorice shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-licorice dark:text-white">
                  {title}
                </h2>
                <div className="flex gap-3">
                  <Button onClick={handleCopy} type="button">
                    {copied ? 'Copied!' : 'Copy Code'}
                  </Button>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Close"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Code Display */}
            <div className="flex-1 overflow-auto p-6">
              <pre
                className="bg-gray-900 p-4 rounded-md overflow-x-auto text-sm"
                style={{ backgroundColor: '#282c34', padding: '20px' }}
              >
                <code ref={codeRef} className="language-javascript">
                  {code}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
